<template>
  <div ref="thresholdElement" class="b-leasing-car-config">
    <div class="b-leasing-car-config__header mb-24">
      <div class="h2 extrabold">
        {{ dictionary.monthlyRentalLabel }}
      </div>
      <div>
        <div
          class="b-leasing-car-config__price-w-vat"
          :class="{ 'is-active': vatIncluded }">
          <div class="bold mb-5">
            <span class="animate">
              <em v-if="vatIncluded">
                {{ calculatedPricePerMonthIncldVat }}
              </em>
              <em v-else>{{ calculatedPricePerMonth }}</em>
              <span class="suffix">
                {{ dictionary.vehiclePriceSuffix }}
              </span>
            </span>
          </div>

          <div class="form-check form-switch">
            <label
              class="form-check-label"
              :class="{
                defaultCursor: priceHasValue(pricePerMonthVatIncluded),
              }"
              :for="idPrefix + '-vat'">
              <span v-if="vatIncluded">
                {{ dictionary.vehicleVatIncluded }}
              </span>
              <span v-else>
                {{ dictionary.vehicleVatExcluded }}
              </span>
            </label>
            <input
              v-if="displayVatToggle"
              :id="idPrefix + '-vat'"
              v-model="vatIncluded"
              class="form-check-input"
              type="checkbox"
              role="switch" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasPrepayment || hasPaymentInAdvance" class="mb-24">
      <div class="form-check form-switch">
        <label
          class="form-check-label form-check-label--tooltip"
          :for="idPrefix + '-paymentInAdvanced'"
          data-bs-toggle="tooltip"
          :title="dictionary.paymentInAdvanceTooltipText">
          <span>{{ dictionary.paymentInAdvanceToggleLabel }}</span>
          <LazyImage
            v-if="dictionary.paymentInAdvanceTooltipText"
            :image="{ url: '/imgs/icons/tooltip.svg' }"
            width="16" />
        </label>
        <input
          :id="idPrefix + '-paymentInAdvanced'"
          v-model="withPrepayment"
          class="form-check-input"
          type="checkbox"
          role="switch" />
      </div>
    </div>

    <hr class="separator theme-yellow mb-12" />

    <!-- item -->
    <div
      v-if="(hasPrepayment || hasPaymentInAdvance) && withPrepayment"
      class="mb-24">
      <div class="b-leasing-car-config__pair">
        <em class="h4">
          {{ dictionary.asidePaymentInAdvanceLabel }}
        </em>
        <span v-if="!vatIncluded">
          {{ computedPaymentInAdvance }}
        </span>
        <span v-else-if="vatIncluded && hasPrepayment" class="highlighted">
          {{ computedPrepaymentVatIncluded }}
        </span>
        <span v-else>
          {{ hasPaymentInAdvance }}
        </span>
      </div>
    </div>
    <!-- item -->
    <div v-if="retailPrice" class="mb-24">
      <div class="b-leasing-car-config__pair">
        <em class="h4">
          {{ dictionary.asideRetailPriceLabel }}
        </em>
        <span>{{ retailPrice }}</span>
      </div>
    </div>
    <!-- item -->
    <div v-if="showRetailPriceBeforeTaxes" class="mb-24">
      <div class="b-leasing-car-config__pair">
        <em class="h4">
          {{ dictionary.asideRetailPriceBeforeTaxesLabel }}
        </em>
        <span>{{ retailPriceBeforeTaxes }}</span>
      </div>
    </div>
    <!-- item -->
    <div class="mb-24">
      <div class="b-leasing-car-config__pair">
        <em class="h4">{{ dictionary.numOfPaymentsLabel }}</em>
        <span>
          {{ rentalDuration }} {{ dictionary.numOfPaymentsValueSuffix }}
        </span>
      </div>
    </div>

    <div :class="{ 'mb-24': sponsorsText }">
      <button
        type="button"
        class="btn btn-primary btn-full-width"
        @click="onClickRequestOffer">
        {{ dictionary.buttonRequestOfferText }}
      </button>
    </div>

    <hr v-if="sponsorsText" class="separator mb-24" />

    <div v-if="sponsorsText" class="b-leasing-car-config__banners">
      <div><IdeaLottie width="20" /></div>
      <div v-html="sponsorsText"></div>
    </div>
  </div>

  <div ref="stickyElement" class="s-leasing-car-bar">
    <div class="page-container">
      <ul>
        <li>
          <span>{{ dictionary.monthlyRentalLabel }}</span>
          <span v-if="!vatIncluded" class="value">
            {{ calculatedPricePerMonth }}
          </span>
          <span v-else class="value highlighted">
            {{ calculatedPricePerMonthIncldVat }}
          </span>
        </li>
        <li v-if="withPrepayment && prepayment">
          <span>{{ dictionary.paymentInAdvanceLabel }}</span>
          <span v-if="!vatIncluded" class="value">
            {{ prepayment }}
          </span>
          <span v-else class="value highlighted">
            {{ prepaymentVatIncluded }}
          </span>
        </li>
        <li v-if="retailPrice">
          <span>{{ dictionary.retailPriceLabel }}</span>
          <span class="value">{{ retailPrice }}</span>
        </li>
        <li v-if="showRetailPriceBeforeTaxes">
          <span>{{ dictionary.retailPriceBeforeTaxesLabel }}</span>
          <span class="value">{{ retailPriceBeforeTaxes }}</span>
        </li>
        <li v-if="dictionary.numOfPaymentsLabel">
          <span>{{ dictionary.numOfPaymentsLabel }}</span>
          <span class="value">
            {{ rentalDuration }} {{ dictionary.numOfPaymentsValueSuffix }}
          </span>
        </li>
        <li>
          <button
            type="button"
            class="btn btn-primary btn-height-short"
            @click="onClickRequestOffer">
            {{ dictionary.buttonRequestOfferText }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import LazyImage from './../components/common/LazyImage.vue'
import IdeaLottie from './../components/lotties/Idea.vue'
import StickyOnScroll from './../../modules/StickyOnScroll.js'

export default {
  components: {
    IdeaLottie,
    LazyImage,
  },
  props: {
    idPrefix: {
      required: false,
      type: String,
      default: '',
    },
  },
  data() {
    return {
      pricePerMonthVatIncluded: '',
      priceVatIncludedWithoutPrepayment: '',
      pricePerMonthVatExcluded: '',
      priceVatExcludedWithoutPrepayment: '',

      retailPrice: '',
      showAlwaysRetailPriceBeforeTaxes: false,
      retailPriceBeforeTaxes: '',
      buyingPrice: '',
      prepayment: '',
      prepaymentVatIncluded: '',
      paymentInAdvance: '',

      rentalDuration: '',

      withPrepayment: true,
      vatIncluded: false,
      sponsorsText: null,
      bookingUrl: '',
    }
  },
  computed: {
    hasPrepayment() {
      return this.prepayment ? this.prepayment : false
    },

    hasPaymentInAdvance() {
      return this.paymentInAdvance ? this.paymentInAdvance : false
    },

    displayVatToggle() {
      if (!this.withPrepayment) {
        return this.priceHasValue(this.priceVatIncludedWithoutPrepayment)
      }

      return this.priceHasValue(this.pricePerMonthVatIncluded)
    },
    computedPrepaymentVatIncluded() {
      if (this.hasPrepayment && this.prepaymentVatIncluded) {
        return this.prepaymentVatIncluded
      }
      return '-'
    },

    computedPaymentInAdvance() {
      if (this.hasPrepayment) {
        return this.hasPrepayment
      } else if (this.hasPaymentInAdvance) {
        return this.hasPaymentInAdvance
      }
      return false
    },

    showRetailPriceBeforeTaxes() {
      if (
        this.showAlwaysRetailPriceBeforeTaxes ||
        (this.retailPriceBeforeTaxes !== '' && this.buyingPrice === '')
      ) {
        return this.retailPriceBeforeTaxes
      }
      return false
    },

    calculatedPricePerMonth() {
      if (this.withPrepayment) {
        return this.pricePerMonthVatExcluded
      }
      return this.priceVatExcludedWithoutPrepayment
    },

    calculatedPricePerMonthIncldVat() {
      if (this.withPrepayment) {
        return this.pricePerMonthVatIncluded
      }
      return this.priceVatIncludedWithoutPrepayment
    },
  },
  mounted() {
    const {
      pricePerMonthVatIncluded,
      priceVatIncludedWithoutPrepayment,
      pricePerMonthVatExcluded,
      priceVatExcludedWithoutPrepayment,
      retailPrice,
      showAlwaysRetailPriceBeforeTaxes,
      retailPriceBeforeTaxes,
      buyingPrice,
      prepayment,
      prepaymentVatIncluded,
      paymentInAdvance,

      rentalDuration,
      sponsorsText,
      bookingUrl,
    } = lib.getData('leasingCarData')

    this.pricePerMonthVatIncluded = pricePerMonthVatIncluded
    this.priceVatIncludedWithoutPrepayment = priceVatIncludedWithoutPrepayment
    this.pricePerMonthVatExcluded = pricePerMonthVatExcluded
    this.priceVatExcludedWithoutPrepayment = priceVatExcludedWithoutPrepayment
    this.retailPrice = retailPrice
    this.showAlwaysRetailPriceBeforeTaxes = showAlwaysRetailPriceBeforeTaxes
    this.retailPriceBeforeTaxes = retailPriceBeforeTaxes
    this.buyingPrice = buyingPrice
    this.prepayment = prepayment
    this.prepaymentVatIncluded = prepaymentVatIncluded
    this.paymentInAdvance = paymentInAdvance

    this.sponsorsText = sponsorsText
    this.rentalDuration = rentalDuration

    this.bookingUrl = bookingUrl

    this.$nextTick(() => {
      this.stickyOnScroll = new StickyOnScroll(
        this.$refs.stickyElement,
        this.$refs.thresholdElement
      )
      lib.iniTooltips(document)
    })
  },
  methods: {
    priceHasValue(price) {
      if (price === null || price === undefined) return false
      if (price.length <= 1) return false

      const priceWithoutEuroSign = parseFloat(price.slice(1))

      return priceWithoutEuroSign > 0
    },
    onClickRequestOffer() {
      window.location.assign(
        `${this.bookingUrl}&paymentInAdvance=${this.withPrepayment ? 1 : 0}`
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.defaultCursor {
  cursor: default;
}
</style>
